import React from "react";
import { Box, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import General from "./Features/General";
import Profile from "./Features/Profile";
import Teams from "./Features/Teams";
import TeamMembers from "./Features/TeamMembers";
import RolesPermission from "./Features/RolesPermission";
import Notifications from "./Features/Notifications";
import TwoFactor from "./Features/TwoFactor";
// import Plans from "./Features/Plans"; // removed from reachability — see SettingsOption.jsx
import Integrations from "./Features/Integrations";
import QuickReplies from "./Features/QuickReplies";
import LifecycleSettings from "./Features/LifecycleSettings";
import ContactProfileSettings from "./Features/ContactProfileSettings";
import AuthView from "../../utils/AuthView";

const SettingsDetails = ({ onBack }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // 0-599px
  const selectedOption = useSelector((state) => state.settings.selectedOption);
  const selectedTeam = useSelector((state) => state.settings.selectedTeam);
  const isTeamDetailView = selectedOption === "Teams" && Boolean(selectedTeam);
  const suppressOuterScroll =
    selectedOption === "Team Members" ||
    selectedOption === "Roles & Permission" ||
    isTeamDetailView;
  const WorkspacePermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Workspace || {}
  );
  const roleName = useSelector((state) => state.userDetails.userDetails?.roleName);
  const isOwner = roleName === "Owner";
  // Integrations: the Owner, or a role granted Roles & Permission -> Integrations -> View.
  const canViewIntegrations = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Integrations?.View === true
  );

  // md+: keep viewport-based cap; xs/sm fill Home outlet (flex) — no 100vh (avoids bottom nav overlap)
  const maxHeightDesktop = "calc(100vh - 104px)";

  const renderComponent = () => {
    switch (selectedOption) {
      case "General":
        return <General />;
      case "Profile":
        return <Profile />;
      case "Teams":
        return <Teams />;
      case "Team Members":
        return <TeamMembers />;
      case "Roles & Permission":
        return <RolesPermission />;
      case "Notification":
        return <Notifications />;
      case "Security":
        return <TwoFactor />;
      case "Integrations":
        return isOwner || canViewIntegrations ? <Integrations /> : <General />;
      // case "Plans" removed from reachability — falls through to default (<General />)
      case "Quick Replies":
        return <QuickReplies />;
      case "Lifecycle":
        return <LifecycleSettings />;
      case "Contact Profile":
        return <ContactProfileSettings />;
      default:
        return <General />;
    }
  };

  // Only show AuthView for General component when user doesn't have Workspace.View permission
  if (selectedOption === "General" && !WorkspacePermissions.View) {
    return (
      <Box
        sx={{
          maxHeight: { xs: "100%", sm: "100%", md: maxHeightDesktop, lg: maxHeightDesktop, xl: maxHeightDesktop },
          height: "100%",
          minHeight: 0,
          flex: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          // Same no-paper-surface treatment as the main wrapper below.
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Header with back button and page name for xs screens only */}
        {isXs && onBack && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "12px 16px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
              gap: "12px",
              flexShrink: 0,
              zIndex: 2,
              position: "relative",
            }}
          >
            <IconButton
              onClick={onBack}
              size="small"
              sx={{
                padding: "4px",
                backgroundColor: theme.palette.background.paper,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "1.2rem",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.1rem",
                fontWeight: 500,
                color: "text.primary",
              }}
            >
              {selectedOption}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            position: "absolute",
            top: isXs && onBack ? "56px" : 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
            borderRadius: "8px",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1,
          }}
        >
          <AuthView />
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {renderComponent()}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxHeight: { xs: "100%", sm: "100%", md: maxHeightDesktop, lg: maxHeightDesktop, xl: maxHeightDesktop },
        height: "100%",
        minHeight: 0,
        flex: 1,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        // No paper surface (background/shadow/border) — Settings content now lays
        // directly on the page's background.default, same as the Inbox/Dashboard
        // panels. Height cap + overflow stay: they drive scrolling, not looks.
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header with back button and page name for xs screens only */}
      {isXs && onBack && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
            gap: "12px",
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={onBack}
            size="small"
            sx={{
              padding: "4px",
              backgroundColor: theme.palette.background.paper,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
              "& .MuiSvgIcon-root": {
                fontSize: "1.2rem",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1.1rem",
              fontWeight: 500,
              color: "text.primary",
            }}
          >
            {selectedOption}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          color: "text.primary",
          ...(selectedOption === "Roles & Permission" ||
          selectedOption === "Team Members" ||
          selectedOption === "Security" ||
          isTeamDetailView
            ? { display: "flex", flexDirection: "column" }
            : {}),
          overflowY: suppressOuterScroll ? "hidden" : "auto",
          overflowX: "hidden",
          scrollbarWidth: suppressOuterScroll
              ? "none"
              : { xs: "none", sm: "none", md: "thin", lg: "thin", xl: "thin" },
          scrollbarColor: (theme) =>
            theme.palette.mode === "dark" ? "#555 #1a1a1a" : "#888 #f1f1f1",
          msOverflowStyle: suppressOuterScroll
              ? "none"
              : { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" },
          "&::-webkit-scrollbar": {
            width: suppressOuterScroll
                ? 0
                : { xs: 0, sm: 0, md: "3px", lg: "3px", xl: "3px" },
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#555" : "#888",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#777" : "#555",
            },
          },
        }}
      >
        {renderComponent()}
      </Box>
    </Box>
  );
};

export default SettingsDetails;
